/**
 * EdgeSAM ORT glue —— 逐行对拍 climb-core/src/sam.rs
 *
 * encode：ResizeLongestSide 1024 + 双线性 + ImageNet 归一化 + 右列零填充。
 *   ⚠️  bug 兼容：上游右填充区写 (0-mean)/std，底部填充区保持原始 0.0（未归一化），
 *   本实现逐行复刻该行为（sam.rs:107-117），两端输出才可对拍。
 * decode：框两角点提示（labels [2,3]）→ 256×256 logits + 置信度。
 */
import type { InferenceSession } from "onnxruntime-web";
import { ort } from "./ort";
import { MASK_GRID } from "../core/samgrid";

export const ENC_IN = 1024;

const MEAN = [0.485, 0.456, 0.406] as const;
const STD = [0.229, 0.224, 0.225] as const;

export interface SamEmbedding {
  /** [256*64*64] CHW */
  data: Float32Array;
  /** 分析分辨率 → 1024 letterbox 缩放系数 */
  scale: number;
}

/** 对拍 sam.rs:70-129 */
export function encodeInput(
  rgba: Uint8ClampedArray,
  w: number,
  h: number,
): { input: Float32Array; scale: number } {
  const scale = ENC_IN / Math.max(w, h);
  const nw = Math.round(w * scale);
  const nh = Math.round(h * scale);
  const input = new Float32Array(3 * ENC_IN * ENC_IN);

  for (let y = 0; y < nh; y++) {
    const sy = Math.min(Math.max((y + 0.5) / scale - 0.5, 0), h - 1);
    const y0 = Math.floor(sy);
    const y1 = Math.min(y0 + 1, h - 1);
    const fy = sy - y0;
    for (let x = 0; x < nw; x++) {
      const sx = Math.min(Math.max((x + 0.5) / scale - 0.5, 0), w - 1);
      const x0 = Math.floor(sx);
      const x1 = Math.min(x0 + 1, w - 1);
      const fx = sx - x0;
      const p00 = (y0 * w + x0) * 4;
      const p01 = (y0 * w + x1) * 4;
      const p10 = (y1 * w + x0) * 4;
      const p11 = (y1 * w + x1) * 4;
      for (let c = 0; c < 3; c++) {
        const v =
          (rgba[p00 + c] * (1 - fx) * (1 - fy) +
            rgba[p01 + c] * fx * (1 - fy) +
            rgba[p10 + c] * (1 - fx) * fy +
            rgba[p11 + c] * fx * fy) /
          255;
        input[c * ENC_IN * ENC_IN + y * ENC_IN + x] = (v - MEAN[c]) / STD[c];
      }
    }
  }
  // 右填充列（x ∈ [nw, 1024)）全部行：归一化黑。底部行（y ∈ [nh,1024), x < nw）保持 0.0
  // —— 与上游 sam.rs:108-117 完全一致（含其不对称行为）
  for (let c = 0; c < 3; c++) {
    const base = c * ENC_IN * ENC_IN;
    const padVal = (0 - MEAN[c]) / STD[c];
    for (let y = 0; y < ENC_IN; y++) {
      for (let x = nw; x < ENC_IN; x++) {
        if (y >= nh || x >= nw) {
          input[base + y * ENC_IN + x] = padVal;
        }
      }
    }
  }
  return { input, scale };
}

/** EdgeSAM 图像编码（一次/图） */
export async function encode(
  session: InferenceSession,
  rgba: Uint8ClampedArray,
  w: number,
  h: number,
): Promise<SamEmbedding | null> {
  if (w === 0 || h === 0) return null;
  const { input, scale } = encodeInput(rgba, w, h);
  const tensor = new ort.Tensor("float32", input, [1, 3, ENC_IN, ENC_IN]);
  const results = await session.run({ image: tensor });
  const emb = results["image_embeddings"];
  const n = 256 * 64 * 64;
  if ((emb.data as Float32Array).length < n) return null;
  return {
    data: (emb.data as Float32Array).slice(0, n),
    scale,
  };
}

/** 框提示解码 → (256×256 logits, SAM 置信度)。对拍 sam.rs:133-153 */
export async function maskForBox(
  session: InferenceSession,
  emb: SamEmbedding,
  box: readonly [number, number, number, number],
): Promise<{ grid: Float32Array; conf: number } | null> {
  const coords = new Float32Array([
    box[0] * emb.scale,
    box[1] * emb.scale,
    box[2] * emb.scale,
    box[3] * emb.scale,
  ]);
  const labels = new Float32Array([2, 3]);
  const results = await session.run({
    image_embeddings: new ort.Tensor("float32", emb.data, [1, 256, 64, 64]),
    point_coords: new ort.Tensor("float32", coords, [1, 2, 2]),
    point_labels: new ort.Tensor("float32", labels, [1, 2]),
  });
  const scores = results["scores"];
  const masks = results["masks"];
  const dims = masks.dims as readonly number[];
  if (
    dims.length !== 4 ||
    dims[2] !== MASK_GRID ||
    dims[3] !== MASK_GRID
  ) {
    return null;
  }
  const conf = (scores.data as Float32Array)[0] ?? 0;
  const grid = (masks.data as Float32Array).slice(0, MASK_GRID * MASK_GRID);
  return { grid, conf };
}
